/**
 * This file manages rewards state and logic using Redux Toolkit's createSlice and createAsyncThunk.
 * It handles fetching, creating, and deleting rewards, and stores them in Redux state.
 * State is persisted in Redux and interacts with the rewards API for backend operations.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { rewardsAPI } from '../services/rewardsApi';
import { createLogger } from '../../../shared/config/logger';
import { AuthError } from '@supabase/supabase-js';
import { addAsyncHandlers } from '../../../shared/store/utils/sliceHelpers';
import { extractErrorMessage } from '../../../shared/utils/errorHandling';

const logger = createLogger('rewardsSlice.js');

/**
 * Initial state for rewards
 * @property {Object} rewards - Stores rewards as an object keyed by reward ID
 * @property {Object} analytics - Stores rewards analytics as an object
 * @property {boolean} loading - Indicates if an async action is in progress
 * @property {string|null} error - Stores error messages
 * @property {boolean} hasLoaded - Track if rewards have been loaded at least once
 */
export const initialState = {
    rewards: {}, // Stores rewards as an object keyed by reward ID
    analytics: {}, // Stores rewards analytics as an object
    loading: false, // Indicates if an async action is in progress
    error: null, // Stores error messages
    hasLoaded: false, // Track if rewards have been loaded at least once
};

/**
 * Thunk for fetching all rewards for a user
 * Calls the rewards API and stores the result in Redux state.
 */
export const fetchRewards = createAsyncThunk(
    'rewards/fetch',
    async (_, { rejectWithValue }) => {
        try {
            logger.info('Fetching rewards for authenticated merchant');
            const response = await rewardsAPI.getAllRewards();
            // Handle wrapped response: { data: [...] }
            const rewardsArray = response.data?.data || response.data || [];
            // Convert array to object with id as key for efficient access
            const rewardsObject = rewardsArray.reduce((acc, reward) => {
                acc[reward.id] = reward;
                return acc;
            }, {});
            return rewardsObject;
        } catch (error) {
            // Handle and log errors
            const errorMessage =
                error instanceof AuthError
                    ? error.message
                    : error instanceof Error
                      ? error.message
                      : 'Unknown error, failed to fetch rewards';
            logger.error('Fetch rewards failed', { error: errorMessage });
            return rejectWithValue({ error: errorMessage });
        }
    }
);

/**
 * Thunk for fetching all rewards analytics for a user
 * Calls the rewards API and stores the result in Redux state.
 */
export const fetchAnalytics = createAsyncThunk(
    'rewards/fetchAnalytics',
    async (userId, { rejectWithValue }) => {
        // userId accepted for backwards compatibility with existing call sites;
        // analytics are fetched for the authenticated merchant via API token.
        try {
            logger.info('Fetching analytics for merchant');

            const response = await rewardsAPI.getRewardAnalytics();
            const data = response.data ?? response;

            logger.debug('Returning analytics:', data);

            return {
                totalRedemptions: data.totalRedemptions ?? 0,
                pointsSpent: data.pointsSpent ?? 0,
                topReward: data.topReward ?? 'N/A',
                topRewardRedemptions: data.topRewardRedemptions ?? 0,
            };
        } catch (error) {
            const errorMessage =
                error instanceof AuthError
                    ? error.message
                    : error instanceof Error
                      ? error.message
                      : 'Unknown error, failed to fetch reward analytics';
            logger.error('Fetch rewards analytics failed', { error: errorMessage });
            return rejectWithValue({ error: errorMessage });
        }
    }
);

/**
 * Thunk for creating a new reward
 * Calls the rewards API to create a reward and updates Redux state.
 */
export const createReward = createAsyncThunk(
    'rewards/create',
    async (form, { rejectWithValue }) => {
        try {
            logger.info('Creating new reward', { form });
            const response = await rewardsAPI.createReward(form);
            return response.data;
        } catch (error) {
            // Extract error message using utility function
            const errorMessage = extractErrorMessage(error);
            logger.error('Create reward failed', { error: errorMessage, form, originalError: error });
            return rejectWithValue({ error: errorMessage });
        }
    }
);

/**
 * Thunk for deleting a reward by ID
 * Calls the rewards API to delete a reward and updates Redux state.
 */
export const deleteReward = createAsyncThunk(
    'rewards/delete',
    async ({ id }, { rejectWithValue }) => {
        try {
            logger.info('Deleting reward', { id });
            const response = await rewardsAPI.deleteReward(id);
            return { id, data: response.data };
        } catch (error) {
            // Handle and log errors
            const errorMessage =
                error instanceof AuthError
                    ? error.message
                    : error instanceof Error
                      ? error.message
                      : 'Unknown error, failed to delete reward';
            logger.error('Delete reward failed', { error: errorMessage, id });
            return rejectWithValue({ error: errorMessage });
        }
    }
);

/**
 * The rewards slice, containing reducers and extraReducers for async thunks
 * Handles all async thunk states (pending, fulfilled, rejected) for each rewards action
 */
const rewardsSlice = createSlice({
    name: 'rewards',
    initialState,
    reducers: {
        // Synchronous reducer to clear error state
        clearErrors: (state) => {
            state.error = null;
        },
        // Synchronous reducer to reset rewards state
        resetRewards: (state) => {
            state.rewards = {};
            state.hasLoaded = false;
            state.error = null;
        },
        clearAnalytics: (state) => {
            state.analytics = {};
            state.loading = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Use helpers for standard patterns
        addAsyncHandlers(builder, fetchAnalytics, 'analytics');
        
        // Custom handler for fetchRewards (has hasLoaded flag)
        builder
            .addCase(fetchRewards.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchRewards.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.rewards = action.payload;
                state.hasLoaded = true;
            })
            .addCase(fetchRewards.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.error || action.error?.message || 'Failed to fetch rewards';
            });
        
        // Custom handler for createReward (adds to rewards object)
        builder
            .addCase(createReward.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createReward.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                state.rewards[action.payload.id] = action.payload;
            })
            .addCase(createReward.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.error || action.error?.message || 'Failed to create reward';
            });
        
        // Custom handler for deleteReward (removes from rewards object)
        builder
            .addCase(deleteReward.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteReward.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                delete state.rewards[action.payload.id];
            })
            .addCase(deleteReward.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.error || action.error?.message || 'Failed to delete reward';
            });
    },
});

// Export synchronous actions
export const { clearErrors, resetRewards, clearAnalytics } = rewardsSlice.actions;
// Export reducer for store configuration
export default rewardsSlice.reducer;

/**
 * API OBJECT - Convenient access to all reward-related actions (thunks)
 */
export const rewardsSliceAPI = {
    fetchAnalytics,
    fetchRewards,
    createReward,
    deleteReward,
};
